import { mediaQueryWidth } from '@core/styles/helpers'
import styled from '@emotion/styled'
import { Avatar, Drawer, Layout } from 'antd'
import { Link } from 'react-router-dom'
import { darken, getContrast } from 'polished'

const sidebarColor = ({ configTheme, theme }) =>
  configTheme?.mainColorCode ||
  theme.layout.management.subHeader.backgroundColor
const sidebarInk = (props) =>
  getContrast(sidebarColor(props), '#000000') >
  getContrast(sidebarColor(props), '#ffffff')
    ? '#000000'
    : '#ffffff'

const headerColor = ({ configTheme, theme }) =>
  configTheme?.topColorCode || theme.layout.management.header.backgroundColor
const headerInk = (props) =>
  getContrast(headerColor(props), '#000') >
  getContrast(headerColor(props), '#fff')
    ? '#000'
    : '#fff'

const sidebarIconColor = (props) => {
  const main = sidebarColor(props)
  let color = main
  for (let step = 1; step <= 100 && getContrast(color, '#ffffff') < 3; step++) {
    color = darken(step / 100, main)
  }
  return color
}

export const Wrapper = styled(Layout, {
  shouldForwardProp: (prop) => prop !== 'configTheme'
})`
  height: 100%;
  min-height: 0;
  flex-direction: row;
  gap: 0;
  padding: 0;
  background: ${sidebarColor};
  @media (max-width: 599px) {
    padding: 0;
    gap: 0;
  }
`

export const Workspace = styled(Layout)`
  min-width: 0;
  min-height: 0;
  background: var(--ess-page-background);
  padding: 0;
  overflow: hidden;
`

export const Header = styled(Layout.Header, {
  shouldForwardProp: (prop) => prop !== 'configTheme'
})`
  --admin-header-foreground: ${headerInk};
  height: 64px;
  flex: 0 0 64px;
  padding: 0 16px;
  background: ${headerColor};
  color: var(--admin-header-foreground);
  .admin-header {
    gap: 12px;
    min-width: 0;
  }
  .header-brand {
    min-width: 0;
    gap: 12px;
  }
  .header-brand > * {
    margin-right: 0;
  }
  .header-system-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }
  .header-account {
    min-width: 0;
  }
  .header-user-name {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  && .admin-sidebar-toggle .ess-icon {
    display: block;
  }
  .header-locale {
    display: block !important;
  }
  @media (max-width: 599px) {
    padding: 0 4px;
    .header-system-name,
    .header-user-name {
      display: none;
    }
    .header-account {
      gap: 8px;
    }
    .locale-content {
      margin: 0 8px;
    }
  }
`

export const SidebarToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  height: 40px;
  border: 1px solid #dedbe7;
  border-radius: 10px;
  color: #302a3c;
  background: #ffffff;
  cursor: pointer;
  .ess-icon {
    color: inherit;
  }
  &:hover {
    background: #e9e6ef;
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }
`

export const Sider = styled(Layout.Sider)`
  display: none;
  transition: width 240ms ease, min-width 240ms ease, max-width 240ms ease,
    flex-basis 240ms ease;
  background: transparent !important;
  border-radius: 0;
  overflow: hidden;
  .ant-layout-sider-children {
    height: 100%;
  }
  ${mediaQueryWidth('md')} {
    display: block;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SidebarBody = styled('div', {
  shouldForwardProp: (prop) => prop !== 'configTheme'
})`
  --sidebar-main: ${sidebarColor};
  --sidebar-ink: ${sidebarInk};
  --sidebar-icon-active: ${sidebarIconColor};
  background: var(--sidebar-main);
  color: var(--sidebar-ink);
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 22px 14px;
  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 6px 32px;
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 500;
    white-space: nowrap;
  }
  .sidebar-brand .ant-image {
    flex-shrink: 0;
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
  }
  .sidebar-section-heading {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 26px;
    margin: 0 6px 14px;
    overflow: hidden;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: var(--sidebar-ink);
  }
  &[data-compact='true'] .sidebar-section-heading::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: currentColor;
    opacity: 0.35;
  }
  nav {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 3px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .sidebar-copyright {
    flex-shrink: 0;
    margin-top: 16px;
    padding: 8px 0 0;
    text-align: center;
    color: var(--sidebar-ink);
    font-size: 12px;
    line-height: 20px;
  }
  &[data-compact='true'] .sidebar-copyright {
    font-size: 10px;
    line-height: 16px;
    span {
      display: block;
    }
  }
  .sidebar-label {
    flex-shrink: 0;
    white-space: nowrap;
    opacity: 1;
    visibility: visible;
    transition: opacity 140ms ease 100ms, visibility 0s;
  }
  &[data-compact='true'] {
    .sidebar-label {
      opacity: 0;
      visibility: hidden;
      transition: opacity 100ms ease, visibility 0s 100ms;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .sidebar-label,
    &[data-compact='true'] .sidebar-label {
      transition: none;
    }
  }
`

export const SiderNavigation = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'active'
})`
  display: flex;
  gap: 12px;
  align-items: center;
  height: 44px;
  margin-bottom: 10px;
  padding: 4px 3px;
  border-radius: 10px;
  font-size: 13px;
  color: ${({ active }) =>
    active ? 'var(--sidebar-main)' : 'var(--sidebar-ink)'};
  background: ${({ active }) =>
    active ? 'var(--sidebar-ink)' : 'transparent'};
  .sidebar-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border: 1px solid #ffffff;
    border-radius: 9px;
    background: #ffffff;
    color: ${({ active }) =>
      active ? 'var(--sidebar-icon-active)' : '#34343c'};
    .ess-icon {
      display: inline-flex;
      color: inherit;
    }
  }
  > .sidebar-label {
    width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${mediaQueryWidth('lg')} {
    font-size: 15px;
    .sidebar-icon svg {
      width: 24px;
      height: 24px;
    }
  }
  &:hover,
  &:focus {
    color: var(--sidebar-main);
    background: var(--sidebar-ink);
    .sidebar-icon {
      color: var(--sidebar-icon-active);
    }
  }
  &:focus-visible {
    outline: 2px solid var(--sidebar-ink);
    outline-offset: 2px;
  }
`

export const SiderAvatar = styled(Avatar)`
  flex-shrink: 0;
  border: 1px solid currentColor;
`
export const SiderName = styled.div`
  font-size: 13px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
export const SiderEmail = styled.div`
  font-size: 11px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const SiderDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    overflow: hidden;
  }
  .ant-drawer-body > div {
    height: 100%;
  }
  .ant-drawer-header {
    padding-right: 54px;
  }
`

export const Content = styled(Layout.Content)`
  min-width: 0;
  min-height: 0;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &:hover,
  &:focus-within {
    scrollbar-color: #8c849b transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 6px;
  }
  &:hover::-webkit-scrollbar-thumb,
  &:focus-within::-webkit-scrollbar-thumb {
    background: #8c849b;
  }
  background: var(--ess-page-background);
`

export const NoAccessWrapper = styled.div`
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`
